import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  isSameDay,
  isSameWeek,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { DueRow } from './myProjectsMobile.utils';

const WEEK = { weekStartsOn: 1 } as const; // Monday, as CalendarView.jsx

type MyProjectsMobileWeekProps = {
  rows: DueRow[];
  /** The selected day's agenda in the chosen order. */
  sortAgenda: (agenda: DueRow[]) => DueRow[];
  renderCard: (row: DueRow, key: string) => React.ReactNode;
};

/**
 * SW2-2467. The calendar view on a phone: a 7-day strip that fits the width, and the selected day's
 * inspections listed below it as cards. Same week/month navigation and the same exact-date match as
 * the desktop CalendarView (`dueDate === format(day, 'MM-dd-yyyy')`).
 */
const MyProjectsMobileWeek: React.FC<MyProjectsMobileWeekProps> = ({ rows, sortAgenda, renderCard }) => {
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState(() => new Date());

  const weekStart = startOfWeek(anchor, WEEK);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const rowsOn = (day: Date) =>
    rows.filter((row) => row.dueDate != null && row.dueDate.toString() === format(day, 'MM-dd-yyyy'));

  // Keep the selected day inside the visible week: today if the week has it, else its Monday.
  const move = (shift: (date: Date, amount: number) => Date) => {
    const next = shift(anchor, 1);

    setAnchor(next);
    setSelected(isSameWeek(next, new Date(), WEEK) ? new Date() : startOfWeek(next, WEEK));
  };

  const agenda = sortAgenda(rowsOn(selected));

  return (
    <div className='my-projects-mobile__week'>
      <div className='my-projects-mobile__week-nav'>
        <button type='button' aria-label='Previous month' onClick={() => move(subMonths)}>
          <FontAwesomeIcon icon={faAngleDoubleLeft} aria-hidden='true' />
        </button>
        <button type='button' aria-label='Previous week' onClick={() => move(subWeeks)}>
          <FontAwesomeIcon icon={faAngleLeft} aria-hidden='true' />
        </button>
        <span className='my-projects-mobile__week-label' aria-live='polite'>
          {format(anchor, 'MMMM yyyy')}
        </span>
        <button type='button' aria-label='Next week' onClick={() => move(addWeeks)}>
          <FontAwesomeIcon icon={faAngleRight} aria-hidden='true' />
        </button>
        <button type='button' aria-label='Next month' onClick={() => move(addMonths)}>
          <FontAwesomeIcon icon={faAngleDoubleRight} aria-hidden='true' />
        </button>
      </div>

      <div
        className='my-projects-mobile__days'
        role='group'
        aria-label={`Week of ${format(weekStart, 'MMMM d')}`}
      >
        {days.map((day) => {
          const count = rowsOn(day).length;

          return (
            <button
              key={day.toISOString()}
              type='button'
              className={isSameDay(day, new Date()) ? 'my-projects-mobile__day--today' : undefined}
              aria-pressed={isSameDay(day, selected)}
              aria-label={`${format(day, 'EEEE, MMMM d')}, ${count} ${count === 1 ? 'inspection' : 'inspections'}`}
              onClick={() => setSelected(day)}
            >
              <span className='my-projects-mobile__day-name'>{format(day, 'EEEEE')}</span>
              <span className='my-projects-mobile__day-number'>{format(day, 'd')}</span>
              <span className='my-projects-mobile__day-count'>{count > 0 ? count : ''}</span>
            </button>
          );
        })}
      </div>

      <p className='my-projects-mobile__agenda-title'>{format(selected, 'EEEE, MMM d')}</p>
      {agenda.length ? (
        <ul className='my-projects-mobile__list'>
          {agenda.map((row, index) => renderCard(row, `${row.projectId}_${row.inspectionId}_${index}`))}
        </ul>
      ) : (
        <p className='my-projects-mobile__empty'>No inspections due this day.</p>
      )}
    </div>
  );
};

export default MyProjectsMobileWeek;
